/**
 * Shared styled audio player for Results review audio.
 *
 * Provides:
 *  - Play/pause toggle
 *  - Skip ±5s controls (QA AC4 fallback while word-level timestamps are unavailable)
 *  - Playback rate selector (0.5× / 0.75× / 1× / 1.25× / 1.5×) for slow re-listening
 *  - A click-to-seek progress bar
 *  - Live elapsed / remaining timestamps populated from the audio element itself
 *    (not the server metadata) so the displayed duration matches reality (fixes B2)
 *
 * @module ResultAudioPlayer
 */

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Pause, Play, Rewind, FastForward, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

const PLAYBACK_RATES: readonly number[] = [0.5, 0.75, 1, 1.25, 1.5];
const SKIP_SECONDS = 5;

interface ResultAudioPlayerProps {
    readonly src: string;
    /**
     * Optional server-reported duration (seconds). Only used as a placeholder
     * before the audio element fires `loadedmetadata`.
     */
    readonly fallbackDurationSeconds?: number | null;
    /** Whether the user is allowed to download the recording (QA B8). */
    readonly allowDownload?: boolean;
    readonly downloadFilename?: string;
    readonly ariaLabel?: string;
    readonly errorMessage?: string;
    readonly className?: string;
}

const formatTime = (seconds: number): string => {
    if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
    const total = Math.floor(seconds);
    const minutes = Math.floor(total / 60);
    const remaining = total % 60;
    return `${minutes}:${remaining.toString().padStart(2, "0")}`;
};

export const ResultAudioPlayer = forwardRef<HTMLAudioElement, ResultAudioPlayerProps>(
function ResultAudioPlayer({
    src,
    fallbackDurationSeconds,
    allowDownload = false,
    downloadFilename,
    ariaLabel,
    errorMessage,
    className,
}: ResultAudioPlayerProps, ref) {
    const { t } = useTranslation();
    const internalRef = useRef<HTMLAudioElement>(null);
    // Expose the underlying <audio> element to callers via forwardRef
    useImperativeHandle(ref, () => internalRef.current as HTMLAudioElement);
    const audioRef = internalRef;
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState<number | null>(null);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [errored, setErrored] = useState(false);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const handleLoadedMetadata = () => {
            if (Number.isFinite(audio.duration)) {
                setDuration(audio.duration);
            }
            setErrored(false);
        };
        const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
        const handleEnded = () => setIsPlaying(false);
        const handlePlay = () => setIsPlaying(true);
        const handlePause = () => setIsPlaying(false);
        const handleError = () => {
            setErrored(true);
            setIsPlaying(false);
        };

        setIsPlaying(false);
        setCurrentTime(0);
        setDuration(null);
        setErrored(false);

        audio.addEventListener("loadedmetadata", handleLoadedMetadata);
        audio.addEventListener("timeupdate", handleTimeUpdate);
        audio.addEventListener("ended", handleEnded);
        audio.addEventListener("play", handlePlay);
        audio.addEventListener("pause", handlePause);
        audio.addEventListener("error", handleError);

        if (Number.isFinite(audio.duration)) {
            handleLoadedMetadata();
        }

        return () => {
            audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
            audio.removeEventListener("timeupdate", handleTimeUpdate);
            audio.removeEventListener("ended", handleEnded);
            audio.removeEventListener("play", handlePlay);
            audio.removeEventListener("pause", handlePause);
            audio.removeEventListener("error", handleError);
        };
    }, [src]);

    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.playbackRate = playbackRate;
        }
    }, [playbackRate]);

    const togglePlay = () => {
        const audio = audioRef.current;
        if (!audio) return;
        if (audio.paused) {
            audio.play().catch(() => setErrored(true));
        } else {
            audio.pause();
        }
    };

    const skip = (delta: number) => {
        const audio = audioRef.current;
        if (!audio) return;
        const target = (audio.currentTime || 0) + delta;
        const safeDuration = duration ?? fallbackDurationSeconds ?? 0;
        audio.currentTime = Math.max(0, safeDuration > 0 ? Math.min(target, safeDuration) : target);
    };

    const onSeekClick = (event: React.MouseEvent<HTMLDivElement>) => {
        const audio = audioRef.current;
        const totalDuration = duration ?? fallbackDurationSeconds ?? 0;
        if (!audio || totalDuration <= 0) return;
        const rect = event.currentTarget.getBoundingClientRect();
        const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
        audio.currentTime = ratio * totalDuration;
    };

    const onSeekKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "ArrowLeft") {
            event.preventDefault();
            skip(-SKIP_SECONDS);
        } else if (event.key === "ArrowRight") {
            event.preventDefault();
            skip(SKIP_SECONDS);
        }
    };

    const effectiveDuration = duration ?? (typeof fallbackDurationSeconds === "number" ? fallbackDurationSeconds : 0);
    const progressPercent = effectiveDuration > 0 ? Math.min(100, (currentTime / effectiveDuration) * 100) : 0;
    const resolvedAriaLabel = ariaLabel ?? t("simulation.results.reviewDialog.audio.playback", {
        defaultValue: "Audio playback",
    });
    const resolvedErrorMessage = errorMessage ?? t("simulation.results.reviewDialog.audio.playbackError", {
        defaultValue: "Unable to play this audio.",
    });

    return (
        <div
            className={cn(
                "w-full max-w-full overflow-hidden rounded-lg border border-primary/15 bg-muted/40 p-3 shadow-sm",
                className,
            )}
            data-audio-protected
        >
            <audio
                ref={audioRef}
                src={src}
                preload="metadata"
                aria-label={resolvedAriaLabel}
                onContextMenu={(e) => e.preventDefault()}
                onDragStart={(e) => e.preventDefault()}
                className="sr-only"
            >
                <track kind="captions" />
            </audio>

            <div className="grid grid-cols-[auto_auto_auto_minmax(4rem,1fr)_auto] items-center gap-1.5 sm:gap-2">
                <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => skip(-SKIP_SECONDS)}
                    className="h-8 w-8 shrink-0 text-muted-foreground hover:bg-primary/10 hover:text-foreground"
                    aria-label={t("simulation.results.reviewDialog.audio.rewind", {
                        seconds: SKIP_SECONDS,
                        defaultValue: `Rewind ${SKIP_SECONDS} seconds`,
                    })}
                    title={t("simulation.results.reviewDialog.audio.rewindShort", {
                        seconds: SKIP_SECONDS,
                        defaultValue: `Rewind ${SKIP_SECONDS}s`,
                    })}
                    disabled={errored}
                >
                    <Rewind className="h-4 w-4" />
                </Button>

                <Button
                    type="button"
                    size="icon"
                    onClick={togglePlay}
                    className="h-9 w-9 shrink-0 rounded-full bg-primary text-primary-foreground shadow-sm hover:bg-primary/90"
                    aria-label={
                        isPlaying
                            ? t("simulation.results.reviewDialog.audio.pause", { defaultValue: "Pause audio" })
                            : t("simulation.results.reviewDialog.audio.play", { defaultValue: "Play audio" })
                    }
                    disabled={errored}
                >
                    {isPlaying ? (
                        <Pause className="h-4 w-4" />
                    ) : (
                        <Play className="h-4 w-4 translate-x-[1px]" />
                    )}
                </Button>

                <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => skip(SKIP_SECONDS)}
                    className="h-8 w-8 shrink-0 text-muted-foreground hover:bg-primary/10 hover:text-foreground"
                    aria-label={t("simulation.results.reviewDialog.audio.forward", {
                        seconds: SKIP_SECONDS,
                        defaultValue: `Forward ${SKIP_SECONDS} seconds`,
                    })}
                    title={t("simulation.results.reviewDialog.audio.forwardShort", {
                        seconds: SKIP_SECONDS,
                        defaultValue: `Forward ${SKIP_SECONDS}s`,
                    })}
                    disabled={errored}
                >
                    <FastForward className="h-4 w-4" />
                </Button>

                <div
                    role="slider"
                    tabIndex={0}
                    aria-label={t("simulation.results.reviewDialog.audio.seek", { defaultValue: "Seek audio" })}
                    aria-valuemin={0}
                    aria-valuemax={effectiveDuration > 0 ? Math.round(effectiveDuration) : 0}
                    aria-valuenow={Math.round(currentTime)}
                    onClick={onSeekClick}
                    onKeyDown={onSeekKeyDown}
                    className={cn(
                        "group relative h-2 min-w-0 cursor-pointer rounded-full bg-primary/15 transition-colors hover:bg-primary/20",
                        errored && "cursor-not-allowed opacity-40",
                    )}
                >
                    <div
                        className="absolute inset-y-0 left-0 rounded-full bg-primary transition-[width]"
                        style={{ width: `${progressPercent}%` }}
                    />
                </div>

                <div className="font-ui shrink-0 select-none text-[11px] tabular-nums text-muted-foreground">
                    {formatTime(currentTime)} / {formatTime(effectiveDuration)}
                </div>
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                <div className="flex min-w-0 flex-wrap items-center gap-1">
                    <span className="font-ui mr-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                        {t("simulation.results.reviewDialog.audio.speed", { defaultValue: "Speed" })}
                    </span>
                    {PLAYBACK_RATES.map((rate) => (
                        <button
                            key={rate}
                            type="button"
                            onClick={() => setPlaybackRate(rate)}
                            aria-pressed={playbackRate === rate}
                            aria-label={t("simulation.results.reviewDialog.audio.setSpeed", {
                                rate,
                                defaultValue: `Set playback speed to ${rate}×`,
                            })}
                            className={cn(
                                "min-w-[2.25rem] rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums transition-colors",
                                playbackRate === rate
                                    ? "bg-primary text-primary-foreground"
                                    : "text-muted-foreground hover:bg-primary/10 hover:text-foreground",
                            )}
                        >
                            {rate}×
                        </button>
                    ))}
                </div>

                {allowDownload && !errored && (
                    <a
                        href={src}
                        download={downloadFilename || "answer-recording"}
                        className="font-ui ml-auto inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground hover:bg-primary/10 hover:text-foreground"
                        aria-label={t("simulation.results.reviewDialog.audio.download", {
                            defaultValue: "Download recording",
                        })}
                    >
                        <Download className="h-3 w-3" />
                        {t("simulation.results.reviewDialog.audio.save", { defaultValue: "Save" })}
                    </a>
                )}
            </div>

            {errored && (
                <div className="mt-3 font-ui rounded-md border border-destructive/20 bg-destructive/5 px-2 py-1 text-xs text-destructive">
                    {resolvedErrorMessage}
                </div>
            )}
        </div>
    );
});

ResultAudioPlayer.displayName = "ResultAudioPlayer";

export const StudentAnswerAudioPlayer = forwardRef<HTMLAudioElement, ResultAudioPlayerProps>(
    function StudentAnswerAudioPlayer(props, ref) {
        const { t } = useTranslation();
        return (
            <ResultAudioPlayer
                ref={ref}
                {...props}
                errorMessage={props.errorMessage ?? t("simulation.results.reviewDialog.audio.recordingError", {
                    defaultValue: "Unable to play this recording.",
                })}
                ariaLabel={props.ariaLabel ?? t("simulation.results.reviewDialog.audio.studentRecording", {
                    defaultValue: "Student answer recording",
                })}
            />
        );
    }
);

StudentAnswerAudioPlayer.displayName = "StudentAnswerAudioPlayer";
